import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { DotLottie } from '@lottiefiles/dotlottie-react';
import { Transaction } from '@mysten/sui/transactions';
import { useQueryClient } from '@tanstack/react-query';

import { CASINO_CONFIG } from '@/constants/game-settings';
import { APP_CFG } from '@/configs';
import { useAuth } from '@/hooks';
import { useBoundedNumberStorage } from '@/hooks/useBoundedNumberStorage';
import { useCurrency } from './useCurrency';
import { useCustomToast } from './use-suigar-toast';
import { useGameTransactionController } from './useGameTransactionController';
import { useListOfObjects } from './use-list-of-objects';
import { usePendingReveal } from './usePendingReveal';
import { useTopGameActivities } from './useTopGameActivities';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { useDepositModalStore } from '@/stores/deposit-modal-store';
import configBallsData from '@/pages/plinko_config_balls.json';
import { configMultipliersData } from '@/pages/plinko_config_multipliers';
import { useCurrencyStore } from '@/stores/currency-store';
import { filterPendingActivities } from '@/utils/activity';
import { normalizeSuiErrorMessage, isSignatureInvalidError } from '@/utils/errors';
import { toRoundFixed } from '@/utils';
import type { AutoBetExecutorResult } from './useAutoBetRunner';
import { runStandardGameRound } from './runStandardGameRound';

const {
	global: { defaultBetAmount },
	plinko: {
		bet: { min: MIN_BET, max: MAX_BET },
		rows: { default: DEFAULT_ROWS, min: MIN_ROWS, max: MAX_ROWS },
		defaultRisk: DEFAULT_RISK_LEVEL,
		riskLevels: RISK_LEVELS,
		simulation: { width: SIM_WIDTH, height: SIM_HEIGHT, enabled: ENABLE_SIMULATION },
	},
} = CASINO_CONFIG;

type Coordinate = { startX: number };
type NestedData = { [key: string]: Coordinate[] };
type ConfigBallsData = { [key: string]: NestedData };

const configBalls: ConfigBallsData = configBallsData;

const configMap: Record<string, number> = configMultipliersData.reduce(
	(acc, entry) => {
		const riskKey = entry.risk_level.toLowerCase().replace('suigar_', '');
		acc[`${entry.rows}-${riskKey}`] = entry.id;
		return acc;
	},
	{} as Record<string, number>,
);

const availableRowKeys = Array.from(new Set(configMultipliersData.map((cfg) => cfg.rows)));
const MIN_CONFIG_ROWS = Math.min(...availableRowKeys);
const MAX_CONFIG_ROWS = Math.max(...availableRowKeys);

const clampRowsToAvailable = (rows: number) => Math.min(Math.max(rows, MIN_CONFIG_ROWS), MAX_CONFIG_ROWS);
const EFFECTIVE_MIN_ROWS = Math.max(MIN_ROWS, MIN_CONFIG_ROWS);
const EFFECTIVE_MAX_ROWS = Math.min(MAX_ROWS, MAX_CONFIG_ROWS);

const PLINKO_TOAST_HANDLED = '__plinkoToastHandled';

const markPlinkoToastHandled = (error: unknown) => {
	if (!error || typeof error !== 'object') return;
	(error as Record<string, unknown>)[PLINKO_TOAST_HANDLED] = true;
};

const isPlinkoToastHandled = (error: unknown) => {
	if (!error || typeof error !== 'object') return false;
	return Boolean((error as Record<string, unknown>)[PLINKO_TOAST_HANDLED]);
};

export const getConfigNumber = (rows: number, risk: string): number => {
	const normalizedRisk = risk.toLowerCase();
	const key = `${rows}-${normalizedRisk}`;
	if (configMap[key] != null) return configMap[key];
	// Try clamped rows if original combination is missing
	const clampedKey = `${clampRowsToAvailable(rows)}-${normalizedRisk}`;
	return configMap[clampedKey] ?? configMap[`8-medium`] ?? 7;
};

export const getRowsFromConfigNumber = (configNumber: number): number => {
	const entry = configMultipliersData.find((cfg) => cfg.id === configNumber);
	const rows = entry?.rows ?? MIN_CONFIG_ROWS;
	return clampRowsToAvailable(rows);
};

const PLINKO_STORAGE_KEYS = {
	betAmount: 'suigar.plinko.bet.amount',
	rows: 'suigar.plinko.rows',
	risk: 'suigar.plinko.risk',
} as const;

export const usePlinkoPage = () => {
	const [cookies] = useCookies(['referrer']);
	const toast = useCustomToast();
	const { plinkoActivities, plinkoActivitiesLoading } = useListOfObjects();
	const referrerAddress = cookies.referrer;
	const { selectedCurrency, toBaseUnits, fromBaseUnits } = useCurrency();

	// Game state
	const [rows, setRowsState] = useState<number>(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem(PLINKO_STORAGE_KEYS.rows);
			if (saved) {
				const parsed = parseInt(saved, 10);
				if (Number.isFinite(parsed)) {
					return clampRowsToAvailable(Math.max(EFFECTIVE_MIN_ROWS, Math.min(parsed, EFFECTIVE_MAX_ROWS)));
				}
			}
		}
		return clampRowsToAvailable(DEFAULT_ROWS);
	});
	const setRows = useCallback((value: number) => {
		const clamped = clampRowsToAvailable(value);
		setRowsState(clamped);
		if (typeof window !== 'undefined') {
			localStorage.setItem(PLINKO_STORAGE_KEYS.rows, clamped.toString());
		}
	}, []);
	const [betAmount, setBetAmount] = useBoundedNumberStorage({
		storageKey: PLINKO_STORAGE_KEYS.betAmount,
		defaultValue: defaultBetAmount,
		min: MIN_BET,
		max: MAX_BET,
		round: toRoundFixed,
	});
	const [isBetting, setIsBetting] = useState(false);
	const [riskLevel, setRiskLevelState] = useState<(typeof RISK_LEVELS)[number]>(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem(PLINKO_STORAGE_KEYS.risk);
			if (saved && (RISK_LEVELS as ReadonlyArray<string>).includes(saved)) {
				return saved as (typeof RISK_LEVELS)[number];
			}
		}
		return DEFAULT_RISK_LEVEL;
	});
	const [currentSimRow, setCurrentSimRow] = useState<number>(rows);
	const [simulationActive, setSimulationActive] = useState<boolean>(ENABLE_SIMULATION);
	const [pendingBets, setPendingBets] = useState<string[]>([]);
	const [pipeHeight, setPipeHeight] = useState<number>(0);
	const [pipeWidth, setPipeWidth] = useState<number>(0);
	const [activeBubbles, setActiveBubbles] = useState<Array<{ id: number; size: number; driftX: number; startDelay: number }>>([]);

	const setRiskLevel = useCallback((level: (typeof RISK_LEVELS)[number]) => {
		const next = (RISK_LEVELS as ReadonlyArray<string>).includes(level) ? level : DEFAULT_RISK_LEVEL;
		setRiskLevelState(next);
		if (typeof window !== 'undefined') {
			localStorage.setItem(PLINKO_STORAGE_KEYS.risk, next);
		}
	}, []);

	// Refs
	const currentFrameRef = useRef(0);
	const totalFramesRef = useRef(210);
	const framesPerBallRef = useRef(29);
	const simulationIntervalRef = useRef<number | null>(null);
	const downloadTriggeredRef = useRef(false);
	const simulationDataRef = useRef<{
		[row: number]: { [bin: number]: Array<{ startX: number }> };
	}>({});
	const pipeRef = useRef<HTMLImageElement>(null);
	const plinkoRef = useRef<{
		createBall: (startX: number, startY: number, betId?: string, balanceBetId?: string) => void;
	}>(null);
	const lottieRef = useRef<DotLottie | null>(null);
	const backgroundLottieRef = useRef<DotLottie | null>(null);

	const { finalize, pendingIdsFor } = usePendingReveal();
	const { data: topPlinkoActivities, isLoading: topPlinkoActivitiesLoading } = useTopGameActivities('plinko', { limit: 10 });

	const { selectedCurrency: currencyStore } = useCurrencyStore();
	const { balance, executeTransaction, disconnect, currentAccount } = useAuth();
	const { openLoginModal } = useLoginModalStore();
	const { openDepositModal } = useDepositModalStore();
	const queryClient = useQueryClient();
	const { runGamePlay: runPlinkoGame } = useGameTransactionController('plinko', {
		setTransactionPending: setIsBetting,
		onRequireLogin: openLoginModal,
	});

	const createPlayTransaction = (stake: number, configNumber: number) => {
		const txb = new Transaction();

		const nGames = 1;
		const coin = txb.splitCoins(txb.gas, [nGames * stake]);
		txb.moveCall({
			arguments: [
				txb.object(APP_CFG.PLINKO_PLINKOGAME_SUI_SUI), // plinko game address
				txb.object(APP_CFG.HOUSE_HOUSE_SUI_SUI), // house address
				txb.pure.u8(configNumber), // config number
				coin, // bet coin
				txb.pure.u8(nGames), // number of balls
				txb.object('0x08'), // randomness address
			],
			target: `${APP_CFG.PACKAGE_2}::plinko::place_bet_and_reveal_onchain_randomness`,
			typeArguments: [currencyStore.type],
		});

		return txb;
	};

	const parseResponse = (response: any) => {
		const betEvent = (response as any).events?.pop();
		if (!betEvent || (response as any).effects.status.status == 'failure') {
			const error = normalizeSuiErrorMessage((response as any).effects.status.error, {
				currencySymbol: currencyStore.symbol,
			});
			toast({
				title: 'Transaction Failed',
				description: error,
				status: 'error',
			});
			const handledError = new Error(error);
			markPlinkoToastHandled(handledError);
			throw handledError;
		}

		// Refresh quests after successful game
		if (currentAccount?.address) {
			queryClient.invalidateQueries({
				queryKey: ['questStatuses', 'plinko', currentAccount.address],
			});
		}

		return betEvent.parsedJson;
	};

	// Filter activities to only show those matching current row count
	const shownPlinkoActivities = useMemo(() => filterPendingActivities(plinkoActivities, pendingIdsFor('plinko')), [plinkoActivities, pendingIdsFor]);

	// Calculate profit interval based on current configuration
	const calculateProfitInterval = () => {
		const configIndex = getConfigNumber(rows, riskLevel);
		const multipliers = configMultipliersData?.[configIndex]?.multipliers;

		if (!multipliers || multipliers.length === 0) {
			return { min: 0, max: 0 };
		}

		const minMultiplier = Math.min(...multipliers);
		const maxMultiplier = Math.max(...multipliers);

		const minProfit = betAmount * (minMultiplier - 1);
		const maxProfit = betAmount * (maxMultiplier - 1);

		return {
			min: Math.max(minProfit, -betAmount), // Can't lose more than bet amount
			max: maxProfit,
		};
	};

	// Bubble animation effect
	useEffect(() => {
		const createBubble = () => {
			const newBubble = {
				id: Date.now() + Math.random(),
				size: 2 + Math.random() * 8,
				driftX: (Math.random() - 0.5) * 120,
				startDelay: Math.random() * 0.5,
			};

			setActiveBubbles((prev) => [...prev, newBubble]);

			// Remove bubble after animation completes
			setTimeout(
				() => {
					setActiveBubbles((prev) => prev.filter((bubble) => bubble.id !== newBubble.id));
				},
				5000 + newBubble.startDelay * 1000,
			);
		};

		// Create initial bubbles
		// setTimeout(createBubble, 200);
		// setTimeout(createBubble, 800);
		// setTimeout(createBubble, 1400);

		// Then create new bubbles at intervals
		const interval = setInterval(() => {
			createBubble();
		}, 1200);

		return () => clearInterval(interval);
	}, []);

	// Pre-initialize simulation data on mount
	useEffect(() => {
		for (let r = EFFECTIVE_MIN_ROWS; r <= EFFECTIVE_MAX_ROWS; r++) {
			simulationDataRef.current[r] = {};
			for (let b = 0; b <= r; b++) {
				simulationDataRef.current[r][b] = [];
			}
		}
	}, []);

	// Pipe height measurement effect
	useEffect(() => {
		const node = pipeRef.current;
		if (!node) return;

		const updatePipeMetrics = () => {
			setPipeHeight(node.offsetHeight);
			setPipeWidth(node.offsetWidth);
		};

		updatePipeMetrics();

		const handleLoad = () => updatePipeMetrics();
		if (!node.complete) {
			node.addEventListener('load', handleLoad);
		}

		const observer = new ResizeObserver(updatePipeMetrics);
		observer.observe(node);

		return () => {
			if (!node.complete) {
				node.removeEventListener('load', handleLoad);
			}
			observer.disconnect();
		};
	}, [rows, pipeRef]);

	// Simulation effect
	useEffect(() => {
		if (!ENABLE_SIMULATION || !plinkoRef.current) return;

		setRows(currentSimRow);
		console.log('Starting simulation for row', currentSimRow);
		setSimulationActive(true);

		simulationIntervalRef.current = window.setInterval(() => {
			for (let i = 0; i < 5; i++) {
				const randomOffset = (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 13 + 2);
				const startX = SIM_WIDTH / 2 + randomOffset;
				const startY = 0;
				plinkoRef.current?.createBall(startX, startY);
			}
		}, 200);

		return () => {
			if (simulationIntervalRef.current) {
				clearInterval(simulationIntervalRef.current);
				simulationIntervalRef.current = null;
			}
		};
	}, [currentSimRow, setRows]);

	// Lottie animation setup effect
	useEffect(() => {
		if (lottieRef.current) {
			lottieRef.current.setFrame?.(0);
			lottieRef.current.stop?.();
		}
	}, []);

	useEffect(() => {
		if (backgroundLottieRef.current) {
			backgroundLottieRef.current.stop();
			backgroundLottieRef.current.setFrame(0);
		}
	}, []);

	// Callback from PlinkoGame when a ball has hit a bin
	const handleBallHitBin = (boxIndex: number, startX: number, startY: number, label?: string, balanceBetId?: string) => {
		if (ENABLE_SIMULATION && simulationActive) {
			const curRow = currentSimRow;
			const outOfBounds = boxIndex < 0 || boxIndex > curRow;
			if (outOfBounds) {
				console.warn(`Discarding ball for row ${curRow}: boxIndex ${boxIndex} is outside 0-${curRow}`);
				return;
			}

			const validBoxIndex = boxIndex;
			if (!simulationDataRef.current[curRow] || simulationDataRef.current[curRow][validBoxIndex] === undefined) {
				console.warn(`Received ball for row ${curRow}, bin ${validBoxIndex} which is not initialized. Ignoring.`);
				return;
			}

			if (simulationDataRef.current[curRow][validBoxIndex].length < 50) {
				simulationDataRef.current[curRow][validBoxIndex].push({ startX });
			} else {
				console.log(`Row ${curRow}, bin ${validBoxIndex} already has 50 samples.`);
			}

			let complete = true;
			for (let b = 0; b <= curRow; b++) {
				const binSamples = simulationDataRef.current[curRow][b];
				if (!binSamples || binSamples.length < 20) {
					complete = false;
					console.log('Row', curRow, 'incomplete, bin', b, 'has', binSamples ? binSamples.length : 0, 'samples');
					break;
				}
			}

			if (complete) {
				console.log('Simulation complete for row', curRow);
				if (simulationIntervalRef.current) {
					clearInterval(simulationIntervalRef.current);
					simulationIntervalRef.current = null;
				}
				setTimeout(() => {
					if (curRow < EFFECTIVE_MAX_ROWS) {
						setCurrentSimRow(curRow + 1);
					} else {
						if (!downloadTriggeredRef.current) {
							console.log('All simulations complete. Preparing JSON download.');
							downloadTriggeredRef.current = true;
							setSimulationActive(false);
							const jsonString = JSON.stringify(simulationDataRef.current, null, 2);
							const blob = new Blob([jsonString], { type: 'application/json' });
							const url = URL.createObjectURL(blob);
							const a = document.createElement('a');
							a.href = url;
							a.download = 'plinko_config_balls.json';
							a.style.display = 'none';
							document.body.appendChild(a);
							a.click();
							document.body.removeChild(a);
							URL.revokeObjectURL(url);
						}
					}
				}, 10000);
			}
		} else {
			if (label) {
				setPendingBets((prev) => prev.filter((bet) => bet !== label));
			}
			if (balanceBetId) {
				finalize({ balanceBetId, betId: label });
			}
		}
	};

	type PlinkoRoundOptions = {
		amount: number;
		rows?: number;
		riskLevel?: (typeof RISK_LEVELS)[number];
		validators?: Array<() => boolean>;
		onError?: (error: unknown) => void;
		lightning?: boolean;
	};

	const runPlinkoRound = useCallback(
		async ({
			amount,
			rows: rowsOverride,
			riskLevel: riskOverride,
			validators = [],
			onError,
			lightning = false,
		}: PlinkoRoundOptions): Promise<AutoBetExecutorResult> => {
			const selectedRowsValue = clampRowsToAvailable(rowsOverride ?? rows);
			const selectedRiskValue = riskOverride ?? riskLevel;
			framesPerBallRef.current = lightning ? 100 : 29;

			return runStandardGameRound({
				betAmount: amount,
				runGamePlay: runPlinkoGame,
				toBaseUnits,
				currencyType: selectedCurrency.type,
				validators: [() => !isBetting, ...validators],
				createTransaction: (stakeBaseUnits) => createPlayTransaction(stakeBaseUnits, getConfigNumber(selectedRowsValue, selectedRiskValue)),
				parseResponse,
				getBetId: (bet) => bet.bet_id as string,
				getRewardBaseUnits: (bet) => Number(bet.reward ?? 0),
				getDidWin: (bet) => Boolean(bet.win),
				getPayoutBaseUnits: ({ rewardBaseUnits }) => rewardBaseUnits,
				onBalanceReserved: () => {
					if (lottieRef.current) {
						const startFrame = currentFrameRef.current;
						const endFrame = startFrame + framesPerBallRef.current;
						lottieRef.current.setSpeed(lightning ? 4 : 1);
						lottieRef.current.setSegment(startFrame, endFrame);
						lottieRef.current.play();
						currentFrameRef.current = endFrame;
						if (currentFrameRef.current >= totalFramesRef.current) {
							currentFrameRef.current = 0;
						}
					}
					if (backgroundLottieRef.current) {
						backgroundLottieRef.current.setSpeed(lightning ? 3 : 1);
						backgroundLottieRef.current.setFrame(0);
						backgroundLottieRef.current.play();
					}
				},
				onAfterSuccess: ({ parsed, rewardBaseUnits, stakeBaseUnits, helpers }) => {
					let betId: string | undefined;
					try {
						betId = parsed.bet_id as string | undefined;
						if (!betId) {
							throw new Error('Missing bet id from plinko response.');
						}
						const betConfigNumber = parsed.plinko_config_number;
						const betMultiplierIndex = parsed.multiplier_index;
						const numberOfRows = getRowsFromConfigNumber(betConfigNumber);
						const rowsForAnimation = clampRowsToAvailable(numberOfRows);
						const rowConfig = configBalls[rowsForAnimation];

						if (!rowConfig || !rowConfig[betMultiplierIndex]) {
							throw new Error('Missing plinko animation config for this setup.');
						}

						const ballPositions = rowConfig[betMultiplierIndex];
						const randomIndex = Math.floor(Math.random() * ballPositions.length);
						const startX = ballPositions[randomIndex].startX;
						const startY = 0;

						setPendingBets((prev) => [...prev, betId!]);

						plinkoRef.current?.createBall(startX, startY, betId ?? undefined, helpers.balanceBetId ?? undefined);
					} catch (error) {
						if (betId) {
							setPendingBets((prev) => prev.filter((id) => id !== betId));
						}
						throw error;
					}
				},
				onError: (error) => {
					markPlinkoToastHandled(error);
					if (isSignatureInvalidError(error)) {
						disconnect();
						openLoginModal({ sessionExpired: true });
					} else {
						const description = normalizeSuiErrorMessage(error, {
							currencySymbol: currencyStore.symbol,
							fallback: 'An unknown error occurred',
						});
						toast({
							title: 'Transaction Failed',
							description,
							status: 'error',
						});
					}
					onError?.(error);
				},
			});
		},
		[
			disconnect,
			getConfigNumber,
			getRowsFromConfigNumber,
			isBetting,
			openLoginModal,
			parseResponse,
			plinkoRef,
			rows,
			riskLevel,
			runPlinkoGame,
			selectedCurrency.type,
			currencyStore.symbol,
			setPendingBets,
			toast,
			toBaseUnits,
			configBalls,
			currentFrameRef,
			framesPerBallRef,
			lottieRef,
			backgroundLottieRef,
			totalFramesRef,
		],
	);

	// Betting function
	const handleBet = async (options?: { lightning?: boolean }) => {
		try {
			await runPlinkoRound({
				amount: betAmount,
				rows,
				riskLevel,
				lightning: options?.lightning ?? false,
			});
		} catch (error) {
			if (isPlinkoToastHandled(error)) return;
			const message = error instanceof Error ? error.message : 'Bet failed. Please try again.';
			toast({
				title: 'Plinko bet failed',
				description: message,
				status: 'error',
			});
		}
	};

	return {
		// Data
		plinkoActivities,
		plinkoActivitiesLoading,
		topPlinkoActivities,
		topPlinkoActivitiesLoading,
		shownPlinkoActivities,

		// State
		rows,
		setRows,
		betAmount,
		setBetAmount,
		isBetting,
		riskLevel,
		setRiskLevel,
		pendingBets,

		pipeHeight,
		setPipeHeight,
		pipeWidth,
		setPipeWidth,
		activeBubbles,

		// Refs
		pipeRef,
		plinkoRef,
		lottieRef,
		backgroundLottieRef,
		currentFrameRef,
		totalFramesRef,
		framesPerBallRef,

		// Constants
		MAX_BET,
		MIN_BET,
		SIM_WIDTH,
		SIM_HEIGHT,
		riskLevels: RISK_LEVELS,
		defaultRiskLevel: DEFAULT_RISK_LEVEL,
		rowSettings: {
			min: EFFECTIVE_MIN_ROWS,
			max: EFFECTIVE_MAX_ROWS,
			default: clampRowsToAvailable(DEFAULT_ROWS),
		},

		// Functions
		createPlayTransaction,
		parseResponse,
		calculateProfitInterval,
		handleBallHitBin,
		handleBet,
		runPlinkoRound,

		// Computed
		profitInterval: calculateProfitInterval(),

		// Utils
		fromBaseUnits,
		selectedCurrency,
	};
};
